<script lang="ts" setup>
import { MenuList } from './Menu';
import { siteURL, jncyURL } from '@/configs/Constant';
import { getParams } from '@/utils';
import { onMounted, ref, watch } from 'vue';
import MobileMenu from './mobile/Menu.vue';
import { toSSOLogin, toPersonalCenter, toSSOLogout } from '@/configs/coalEduAuth';
import { fetchQueryList } from '@/apis/commonApi';
import { isMobile } from '@/utils';
import { useRouter } from '@/composables/useRouter';
import { useUserStore } from '@/stores/user';
import { gotoHomePage } from '@/logic/gotoPage';

const userStore = useUserStore();
const currentHtmlName = location.pathname.split('/').pop();
const showBack = ref(false);
const searchValue = ref();
const type = ref(getParams('type'));
const drawer = ref(false);
const { route, router } = useRouter();
const togglePage = (item) => {
  location.href = (item.isPublicDomain ? siteURL : `${jncyURL}`) + item.path;
};
const isHomePage = !currentHtmlName || currentHtmlName === 'index.html';

const goToSearchResults = async () => {
  if ((!currentHtmlName || currentHtmlName === 'index.html') && searchValue.value) {
    try {
      const res = await fetchQueryList({
        pageSize: 1,
        pageIndex: 0,
        q: encodeURIComponent(searchValue.value),
        aggs: true,
        isSkillMaster: 1,
      });
      if (res.status === 1) {
        const firstType = res.data.hits?.[0]?.type || '';
        if (firstType) {
          location.href = `./searchResults.html#/?search=${encodeURIComponent(
            searchValue.value || '',
          )}&type=${firstType}`;
          return;
        }
      }
    } catch (error) {
      console.log(error);
    }
  }
  const pageMap = {
    masterStyle: 'Master',
    workTypeClassroom: 'Course',
    masterClassroom: 'Live',
    specialTraining: 'Special',
    masterDuty: 'Problem',
    masterDetail: 'Master',
  };
  const key = location.pathname.split('/').pop().replace('.html', '');
  location.href = `${jncyURL}/searchResults.html#/?search=${encodeURIComponent(
    searchValue.value || '',
  )}&type=${type.value || pageMap[key] || 'Course'}`;
};

const mobileSearch = () => {
  goToSearchResults();

  drawer.value = false;
};

watch(
  route,
  (r) => {
    showBack.value = !!r.meta?.showBack;
  },
  {
    immediate: true,
  },
);

const back = () => {
  router.value.back();
};
</script>
<template>
  <div
    class="navbar"
    :class="{ 'navbar-m': isMobile }"
    v-class="{ name: isHomePage && 'animate__fadeInDown', duration: 0.5 }"
  >
    <div class="navbar-line"></div>
    <div class="navbar-container">
      <div class="back" v-if="showBack && isMobile" @click="back">
        <i class="el-icon-arrow-left"></i>
      </div>
      <div class="navbar-logo" @click="gotoHomePage" v-else>
        <img src="@/assets/images/jncyLayout/logo.png" />
      </div>
      <div class="navbar-menu" v-if="!isMobile">
        <div
          :class="['menu-item', item.activePath.includes(currentHtmlName) && 'on']"
          v-for="item in MenuList"
          :key="item.label"
          @click="togglePage(item)"
        >
          {{ item.label }}
        </div>
      </div>
      <div class="navbar-right">
        <template v-if="!isMobile">
          <el-input
            placeholder="搜索关键词"
            @keyup.native.enter="goToSearchResults"
            v-model="searchValue"
            class="navbar-input"
          >
            <template #suffix>
              <img
                @click="goToSearchResults"
                src="@/assets/images/jncyLayout/search.png"
                class="navbar-input__icon"
              />
            </template>
          </el-input>
          <div class="navbar-button" @click="toSSOLogin" v-if="!userStore.isLogin">登录注册</div>
          <div class="navbar-userInfo" v-else>
            <div class="navbar-userInfo__name" @click="toPersonalCenter">
              {{ userStore.userInfo.userName }}
            </div>
            <div class="navbar-userInfo__exit" @click="toSSOLogout">退出</div>
          </div>
        </template>
        <template v-else>
          <i class="el-icon-search search" @click="drawer = true"></i>
          <MobileMenu />
        </template>
      </div>
    </div>
    <el-drawer
      :visible.sync="drawer"
      custom-class="search-drawer"
      direction="ttb"
      size="100px"
      :modal="false"
      :append-to-body="true"
    >
      <div class="mobile-search">
        <el-input
          v-model="searchValue"
          placeholder="请输入检索内容"
          clearable
          @keyup.native.enter="goToSearchResults"
        ></el-input>
        <i class="el-icon-search" @click="mobileSearch"></i>
      </div>
    </el-drawer>
  </div>
</template>
<style lang="scss" scoped>
.navbar {
  width: 100%;
  min-width: 1200px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.16);
  overflow: hidden;
  position: relative;
  z-index: 20;
  &-container {
    margin: 0 auto;
    padding: 0 0.2rem;
    display: flex;
    height: 1rem;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
    background: #ffffff;
  }

  &-logo {
    width: 4.6rem;
    height: 0.6rem;
    cursor: pointer;
    display: flex;
    & > img {
      width: 100%;
      height: 100%;
    }
  }
  &-menu {
    margin-left: 0.2rem;
    display: flex;
    .menu-item {
      margin-right: 0.25rem;
      white-space: nowrap;
      cursor: pointer;
      transition: 0.2s ease;
      user-select: none;
      font-size: 0.18rem;
      &.on,
      &:hover {
        color: #536cef;
      }
    }
  }
  .navbar-right {
    display: flex;
    align-items: center;
    height: 0.36rem;
    .search {
      font-size: 24px;
      margin-right: 20px;
      color: #2c44b3;
    }
    .navbar-input {
      flex: 1;
      min-width: 2.5rem;

      &__icon {
        width: 0.16rem;
        height: 0.16rem;
        cursor: pointer;
      }
      &::v-deep {
        .el-input__wrapper {
          background-color: #f8f8f9;
        }
        .el-input__suffix {
          display: flex;
          align-items: center;
          padding-right: 0.1rem;
        }
        .el-input__inner {
          border-radius: 0.04rem;
          font-size: 0.15rem;
          height: 0.4rem;
          &:focus,
          &:active,
          &:visited {
            border-color: #5c50ff !important;
          }
        }
      }
    }
    .navbar-button {
      margin-left: 0.3rem;
      width: 1.1rem;
      height: 0.36rem;
      background: #4562f6;
      border-radius: 0.04rem;
      text-align: center;
      line-height: 0.36rem;
      color: #ffffff;
      font-size: 0.15rem;
      cursor: pointer;
      transition: 0.2s ease;
      user-select: none;
      &:hover {
        opacity: 0.5;
      }
      background: url(@/assets/images/jncyLayout/login-button.png) no-repeat;
      background-size: cover;
    }
  }
}
.fade-enter-active {
  transition: 0.5s ease;
}
.navbar-userInfo {
  display: flex;
  align-items: center;
  margin-left: 0.2rem;
  &__name {
    font-size: 0.15rem;
    white-space: pre-wrap;
    max-width: 1rem;
    word-break: break-word;
    &:hover {
      color: #4562f6;
      cursor: pointer;
    }
  }
  &__exit {
    white-space: nowrap;
    margin-left: 0.4rem;
    font-size: 0.14rem;
    color: #313131;
    opacity: 0.5;
    cursor: pointer;
  }
}
.navbar-line {
  width: 100%;
  height: 0.03rem;
  background: url(@/assets/images/jncyLayout/line.png);
}
</style>
<style scoped lang="scss">
@media (max-width: 768px) {
  .back {
    font-size: 32px;
  }
  .navbar {
    &.navbar-m {
      width: 100%;
      min-width: auto;
      position: fixed;
      top: 0;
      left: 0;
      z-index: 30;
    }
  }
  .mobile-search {
    box-sizing: border-box;
    padding: 0 10px;
    position: relative;
    width: 100%;
    overflow: hidden;
    i {
      font-size: 24px;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 20px;
      color: #2c44b3;
    }
    &::v-deep .el-input {
      .el-input__inner {
        padding-right: 65px;
      }
      .el-input__suffix {
        right: 40px;
      }
    }
  }
}
</style>
